import { Trans, useLingui } from "@lingui/react/macro";
import type { BotConnectionSettings } from "@rakazo/contracts";
import { Switch } from "@rakazo/ui-web";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";
import { useSpaceRpc } from "../../lib/space-rpc";
import { errorText } from "../../lib/user-error";

/** Cobble: the connected accounts a bot may use. Team bots use only the ones switched on here. */
export function BotAccountsSection({ botId }: { botId: string }) {
  const { t } = useLingui();
  const ids = useId();
  const rpc = useSpaceRpc();
  const queryClient = useQueryClient();
  const queryKey = ["cobble", "botConnections", botId] as const;
  const query = useQuery({
    queryKey,
    queryFn: () => rpc.botConnections.get({ botId }),
    retry: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const data = query.data;
  if (!data || data.accounts.length === 0) return null;

  async function save(next: { limited: boolean; connectionIds: string[] }) {
    setBusy(true);
    setError(null);
    try {
      const saved: BotConnectionSettings = await rpc.botConnections.set({ botId, ...next });
      queryClient.setQueryData(queryKey, saved);
    } catch (err) {
      setError(errorText(err, t`Could not save`));
    } finally {
      setBusy(false);
    }
  }

  const allowedIds = data.accounts.filter((account) => account.allowed).map((account) => account.id);
  const canEdit = data.canGrant || data.canRevoke;

  return (
    <section className="mt-6" data-testid="bot-settings-accounts">
      <h3 className="mb-2 text-xs font-medium text-muted-foreground">
        <Trans>Accounts</Trans>
      </h3>
      <div className="space-y-3 rounded-xl bg-muted/50 p-4 [&>*]:mt-0">
        {data.teamBot ? null : (
          <div className="flex min-h-11 items-center justify-between gap-3">
            <label htmlFor={`${ids}-limited`} className="text-sm">
              <Trans>Only the accounts switched on</Trans>
            </label>
            <Switch
              id={`${ids}-limited`}
              checked={data.limited}
              disabled={busy || !data.canGrant}
              onCheckedChange={(limited) => void save({ limited, connectionIds: allowedIds })}
            />
          </div>
        )}
        {data.limited
          ? data.accounts.map((account) => (
              <div key={account.id} className="flex min-h-11 items-center justify-between gap-3">
                <label htmlFor={`${ids}-${account.id}`} className="min-w-0 text-sm">
                  <span className="block truncate">{account.displayName}</span>
                  <span className="block truncate text-xs text-muted-foreground">{account.provider}</span>
                </label>
                <Switch
                  id={`${ids}-${account.id}`}
                  checked={account.allowed}
                  disabled={
                    busy || !canEdit || (account.allowed ? !data.canRevoke : !data.canGrant)
                  }
                  onCheckedChange={(on) =>
                    void save({
                      limited: true,
                      connectionIds: on
                        ? [...allowedIds, account.id]
                        : allowedIds.filter((id) => id !== account.id),
                    })
                  }
                />
              </div>
            ))
          : null}
        {error ? <p className="text-[13px] text-destructive">{error}</p> : null}
      </div>
    </section>
  );
}
